import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { httpGet, httpPost } from "../http";
import { PlusIcon, ShoppingCartIcon, CalendarIcon } from "lucide-react";
import { useToast } from "../components/Toast";
import { DateTime } from "luxon";

export type ShoppingListType = {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
};

type StateType = {
  loaded: boolean;
  lists: ShoppingListType[];
  showCreatePopup: boolean;
  newListName: string;
  creating: boolean;
};

export const ShoppingListIndex = () => {
  const [state, setState] = useState<StateType>({
    loaded: false,
    lists: [],
    showCreatePopup: false,
    newListName: "",
    creating: false,
  });

  const { showError } = useToast();

  const loadLists = useCallback(async () => {
    const result = await httpGet("/api/v1/shopping-lists");

    if (result.error) {
      if (result.error === "logged_out") {
        window.location.href = "/dash/login";
        return;
      }

      showError("Falha ao carregar listas");
      return;
    }

    setState((prev) => ({
      ...prev,
      loaded: true,
      lists: result.body || [],
    }));
  }, [showError]);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  const handleCreateList = async () => {
    if (!state.newListName.trim()) {
      showError("Você precisa escolher um nome");
      return;
    }

    setState((prev) => ({ ...prev, creating: true }));

    const result = await httpPost("/api/v1/shopping-lists", {
      name: state.newListName.trim(),
    });

    if (result.error) {
      setState((prev) => ({ ...prev, creating: false }));
      showError("Falha ao criar lista de compras");
      return;
    }

    setState((prev) => ({
      ...prev,
      showCreatePopup: false,
      newListName: "",
      creating: false,
      lists: [result.body, ...prev.lists],
    }));
  };

  const formatDate = (dateString: string) => {
    return DateTime.fromISO(dateString).toFormat("dd/MM/yyyy");
  };

  if (!state.loaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <ShoppingCartIcon className="h-8 w-8 text-indigo-600" />
            Minhas Listas de Compra
          </h1>
          <button
            onClick={() =>
              setState((prev) => ({ ...prev, showCreatePopup: true }))
            }
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            Nova Lista
          </button>
        </div>

        {state.lists.length === 0 ? (
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <ShoppingCartIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-600 mb-2">
              Nenhuma lista de compras
            </h2>
            <p className="text-gray-500 mb-6">
              Comece criando sua primeira lista!
            </p>
            <button
              onClick={() =>
                setState((prev) => ({ ...prev, showCreatePopup: true }))
              }
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 mx-auto transition-colors"
            >
              <PlusIcon className="h-5 w-5" />
              Criar Lista
            </button>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {state.lists.map((list) => (
              <Link
                key={list.id}
                to={`/dash/shopping-lists/${list.id}`}
                className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">
                      {list.name}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                      <CalendarIcon className="h-4 w-4" />
                      Atualizado em {formatDate(list.updatedAt)}
                    </div>
                  </div>
                  <ShoppingCartIcon className="h-6 w-6 text-gray-400 group-hover:text-indigo-600 transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {state.showCreatePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-[90vw]">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Criar nova lista de compras
            </h2>
            <input
              type="text"
              placeholder="Enter list name..."
              value={state.newListName}
              onChange={(e) =>
                setState((prev) => ({ ...prev, newListName: e.target.value }))
              }
              className="w-full border border-gray-300 rounded-lg px-4 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleCreateList();
                }
              }}
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() =>
                  setState((prev) => ({
                    ...prev,
                    showCreatePopup: false,
                    newListName: "",
                  }))
                }
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                disabled={state.creating}
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateList}
                disabled={state.creating}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {state.creating ? "Criando..." : "Criar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
